import { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

// Permission constants
const GROUPS = {
  ADMIN: 'resolveguru-admins',
  OPS_ENGG: 'resolveguru-ops-engg',
  OPS_SPECIALIST: 'resolveguru-ops-specialist'
};

const useUserPermissions = () => {
  const [userGroups, setUserGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getUserGroups = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get auth session to access the token
        const { tokens } = await fetchAuthSession();
        const accessToken = tokens?.accessToken;

        if (accessToken) {
          // Decode the access token to get groups
          const payload = JSON.parse(atob(accessToken.toString().split('.')[1]));
          
          // Try both cognito:groups (direct Cognito) and custom:groups (SAML)
          const groups = payload['cognito:groups'] || payload['custom:groups'] || [];
          setUserGroups(groups);
        } else {
          setUserGroups([]);
        }
      } catch (err) {
        console.error('Error getting user groups:', err);
        setError(err.message);
        setUserGroups([]);
      } finally {
        setIsLoading(false);
      }
    };

    getUserGroups();
  }, []);

  // Permission helper functions
  const isAdmin = () => userGroups.includes(GROUPS.ADMIN);
  
  const isOpsEngg = () => userGroups.includes(GROUPS.OPS_ENGG);
  
  const isOpsSpecialist = () => userGroups.includes(GROUPS.OPS_SPECIALIST);

  // Check if user can create/edit/delete
  // SWAPPED: ops-specialist now has modify permissions, ops-engg is read-only
  const canModify = () => {
    return isAdmin() || isOpsSpecialist();
  };

  // Check if user has read-only access
  // SWAPPED: ops-engg now has read-only access, ops-specialist can modify
  const isReadOnly = () => {
    return isOpsEngg() && !isAdmin() && !isOpsSpecialist();
  };

  // Get user role display name
  const getUserRole = () => {
    if (isAdmin()) return 'Administrator';
    if (isOpsEngg()) return 'Operations Engineer';
    if (isOpsSpecialist()) return 'Operations Specialist';
    return 'User';
  };

  return {
    userGroups,
    isLoading,
    error,
    // Permission checks
    isAdmin,
    isOpsEngg,
    isOpsSpecialist,
    canModify,
    isReadOnly,
    getUserRole,
    // Constants for external use
    GROUPS
  };
};

export default useUserPermissions;